import React from 'react';
import NumberFormat from 'react-number-format';

// 원가 산출 내역 표/패널이 공유하는 계산 로직, 열 정의, 유효성 검사.
// CostCalcTable(보기전용 표, 편집/확정 공용), CostCalcEditPanel(우측 패널/바텀시트)에서
// 가져다 쓴다. (옵션 B, owner 2026-10-27 승인 - bito feat/ui-modern 29d6fbb를 KMES로 포팅)
//
// [버그수정, 02c0dea] 이전 코드는 이익율을
// "profit/saleVal * (profit > 0 ? 100 : -100)"로 계산했다. profit이 음수면 분수 자체가
// 음수인데 여기에 -100을 다시 곱해 부호가 두 번 뒤집혀, 마이너스 원가(손해)에서도 이익율이
// 양수로 표시됐다(예: 매출 10,000 / 손해 1,000 -> -0.1 * -100 = +10%). 표 리뉴얼과 함께
// "profit/saleVal*100"(부호는 분수에 이미 있다)으로 고치고, 매 렌더마다 formik 값에서
// 순수 계산하도록 뺐다(이전처럼 cost 객체를 렌더 중 직접 mutate하지 않는다).

export const computeRowAmounts = (cost) => {
  const expCnt = Number(cost?.expCnt) || 0;
  const unitVal = Number(cost?.unitVal) || 0;
  const priceVal = Number(cost?.priceVal) || 0;
  const saleVal = expCnt * unitVal;
  const buyVal = expCnt * priceVal;
  const profit = saleVal - buyVal;
  const profitRate = saleVal === 0 ? 0 : (profit / saleVal) * 100;
  return { saleVal, buyVal, profit, profitRate };
};

export const sumCostCalcs = (costCalcs = []) => {
  let totalSales = 0;
  let totalPurchase = 0;
  (costCalcs || []).forEach((cost) => {
    const { saleVal, buyVal } = computeRowAmounts(cost);
    totalSales += saleVal;
    totalPurchase += buyVal;
  });
  const totalProfit = totalSales - totalPurchase;
  const totalMargin = totalSales === 0 ? 0 : (totalProfit / totalSales) * 100;
  return { totalSales, totalPurchase, totalProfit, totalMargin };
};

// 이익율 강조색 - 0% 미만(손해)은 빨강, 10% 미만(저마진)은 주황, 그 외는 기본 텍스트색.
export const profitRateColor = (rate) => {
  const n = Number(rate);
  if (!Number.isFinite(n)) return 'text.primary';
  if (n < 0) return 'error.main';
  if (n < 10) return 'warning.main';
  return 'text.primary';
};

// 원가 산출 한 줄의 필수값 검사 - 보기전용 표의 빨간 마커, 우측 패널의 입력칸 빨간 테두리,
// "원가산출 확정" 버튼의 사전 검증(첫 번째 오류 줄로 패널 열기)이 전부 이 함수를 공유한다.
// 0은 유효한 값(무상 제공 등)이라 "값이 없음"과 구분해서 검사한다.
const isBlank = (v) => v === '' || v === null || v === undefined;
export const validateCostRow = (cost) => {
  const errors = {};
  if (!cost?.productNm || !String(cost.productNm).trim()) {
    errors.productNm = '매출내용(제품 또는 용역명)을 입력하세요';
  }
  if (isBlank(cost?.expCnt) || Number(cost.expCnt) <= 0) {
    errors.expCnt = '수량을 입력하세요';
  }
  if (isBlank(cost?.unitVal)) {
    errors.unitVal = '매출단가를 입력하세요';
  }
  if (isBlank(cost?.priceVal)) {
    errors.priceVal = '매입단가를 입력하세요';
  }
  if (!cost?.sellPartnerNm) {
    errors.sellPartnerNm = '매입처를 선택하세요';
  }
  return errors;
};
export const isCostRowValid = (cost) => Object.keys(validateCostRow(cost)).length === 0;

// 패널 안 일반 입력칸 - 보기 쉬운 테두리를 항상 보여준다(표 안 cellFieldSx와 달리 패널은
// 입력 전용 공간이라 테두리를 숨길 이유가 없다).
export const panelFieldSx = {
  '& .MuiOutlinedInput-input': {
    fontSize: '0.875rem',
  },
};

const CELL_BASE_SX = {
  px: 1,
  py: 1,
  boxSizing: 'border-box',
};

const NUMERIC_CELL_SX = {
  px: 0.75,
  py: 1,
  boxSizing: 'border-box',
};

export const numericCellSx = {
  ...NUMERIC_CELL_SX,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
};

export const headerCellSx = {
  ...CELL_BASE_SX,
  py: 1,
  whiteSpace: 'nowrap',
  fontWeight: 600,
  fontSize: '0.75rem',
  color: 'text.secondary',
  bgcolor: 'grey.50',
};

export const bodyCellSx = { ...CELL_BASE_SX };

// 품목 칸 안 상품명 - 길어도 표가 깨지지 않게 최대 2줄까지만 보여주고 그 이상은
// 말줄임(Tooltip으로 전체 보기).
export const lineClamp2Sx = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

// 옵션 B(보기전용 표 + 우측 패널) 표 열 정의 - 단일 헤더 행(No/품목/수량/매출 단가/매출
// 금액/매입 단가/매입 금액/매입처/이익/이익률) 10열. 삭제 열은 없앴다(삭제는 패널 "품목
// 삭제" 버튼으로 이동) - 편집 표와 확정 표가 완전히 같은 10열을 공유해 CostCalcTable 한
// 컴포넌트로 그린다.
//
// px 값은 1440px 컨테이너 기준(패널 닫힘, 표 전체 폭) 실측 비율 - 품목이 가장 넓고(상품명이
// 핵심 정보), 숫자 열은 글자 수가 뻔해서 좁게. 패널이 열리면 표 카드 폭이 좁아지는데(사이드
// 패널 380px + gap을 빼앗아감), 1280px 창에서도 패널이 닫혀 있는 상태의 표 자체가 가로
// 스크롤되지 않도록 합계 862px로 맞췄다(bito feat/ui-modern 29d6fbb 실측값 그대로 재사용).
export const COST_TABLE_COL_WIDTHS = {
  no: 28,
  item: 210,
  qty: 58,
  sellPrice: 82,
  sellAmount: 90,
  buyPrice: 82,
  buyAmount: 90,
  partner: 86,
  profit: 82,
  profitRate: 54,
};

export const COST_TABLE_COL_KEYS = [
  'no', 'item', 'qty', 'sellPrice', 'sellAmount',
  'buyPrice', 'buyAmount', 'partner', 'profit', 'profitRate',
];

export const COST_TABLE_MIN_WIDTH = COST_TABLE_COL_KEYS.reduce(
  (sum, key) => sum + COST_TABLE_COL_WIDTHS[key],
  0,
);

// colgroup 열 너비를 px 대신 %로 - 표가 min-width보다 넓어지면(패널 닫힌 1440px 등) 브라우저가
// "지정 px 비율대로" 남는 폭을 분배하는데, 이 환산 과정에서 px 반올림이 열마다 쌓이는 문제가
// 있다. %는 컨테이너 폭에서 한 번만 반올림되어 훨씬 덜 어긋난다 - 합이 정확히 100%가 되도록
// 마지막 열에서 나머지를 보정한다.
export const buildCostColPercents = () => {
  const values = COST_TABLE_COL_KEYS.map((key) => COST_TABLE_COL_WIDTHS[key]);
  const total = values.reduce((a, b) => a + b, 0);
  const pct = values.map((v) => (v / total) * 100);
  const sum = pct.reduce((a, b) => a + b, 0);
  pct[pct.length - 1] += 100 - sum;
  return pct.map((p) => `${p.toFixed(4)}%`);
};

// 매입/매출 단가 입력 - 셀 안 모든 숫자에 ₩를 붙이면 번잡해 보여 단가 입력칸은 천단위
// 구분만 하고 통화기호는 합계에만 남긴다. NumberFormatCustom(공용 ₩ 입력칸)과 달리 prefix가
// 없다는 점만 다르다.
export const PriceNumberFormat = React.forwardRef(function PriceNumberFormat(props, ref) {
  const { onChange, isNegative, ...other } = props;
  return (
    <NumberFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({ target: { name: props.name, value: values.value } });
      }}
      thousandSeparator
      isNumericString
      allowNegative={isNegative ? 'true' : 'false'}
      decimalScale={0}
      style={{ textAlign: 'right' }}
    />
  );
});
